/**
 * NetworkMonitor - Sub-component for BuildApkPage
 *
 * Renders the right-side debug terminal with network status and log output.
 */
import React from 'react';
import { Terminal, Wifi } from 'lucide-react';
import { NetworkInfo } from '../../hooks/useNetworkDebug';

interface NetworkMonitorProps {
  debugLog: string;
  networkInfo: NetworkInfo | null;
  autoRefresh: boolean;
  onToggleAutoRefresh: () => void;
  onRefresh: () => void;
  onClearLog: () => void;
}

export const NetworkMonitor: React.FC<NetworkMonitorProps> = ({
  debugLog,
  networkInfo,
  autoRefresh,
  onToggleAutoRefresh,
  onRefresh,
  onClearLog,
}) => {
  return (
    <div className="bg-gray-900 rounded-xl shadow-2xl border-2 border-gray-700 sticky top-4">
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-4 flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <Terminal className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-bold text-white">Monitor de Rede</h3>
        </div>
        <button
          onClick={onToggleAutoRefresh}
          className={`px-3 py-1 rounded text-xs font-semibold ${
            autoRefresh
              ? 'bg-green-500 text-white'
              : 'bg-gray-600 text-gray-300'
          }`}
        >
          {autoRefresh ? '● LIVE' : '○ OFF'}
        </button>
      </div>

      <div className="p-4">
        {/* Network Status Cards */}
        {networkInfo && (
          <div className="space-y-2 mb-4">
            <div className={`p-3 rounded-lg ${
              networkInfo.zerotier?.detected
                ? 'bg-green-900/30 border border-green-500'
                : 'bg-yellow-900/30 border border-yellow-500'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <Wifi className={`w-4 h-4 ${
                  networkInfo.zerotier?.detected ? 'text-green-400' : 'text-yellow-400'
                }`} />
                <span className={`text-sm font-bold ${
                  networkInfo.zerotier?.detected ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  ZeroTier
                </span>
              </div>
              <div className="text-xs text-gray-300">
                {networkInfo.zerotier?.detected
                  ? `IP: ${networkInfo.zerotier.ip}`
                  : 'Não detectado'}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-blue-900/30 border border-blue-500">
              <div className="flex items-center gap-2 mb-1">
                <Wifi className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-bold text-blue-400">Rede Local</span>
              </div>
              <div className="text-xs text-gray-300">
                IP: {networkInfo.local_network?.primary}
              </div>
            </div>
          </div>
        )}

        {/* Log Terminal */}
        <div className="bg-black rounded-lg p-3 h-[400px] overflow-y-auto">
          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
            {debugLog}
          </pre>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            onClick={onRefresh}
            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold"
          >
            🔄 Atualizar
          </button>
          <button
            onClick={onClearLog}
            className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-semibold"
          >
            🗑️ Limpar
          </button>
        </div>
      </div>
    </div>
  );
};
